import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import "./Portfolio.scss";
import { useDispatch } from "react-redux";
import { ATS } from "../../features/ats/atsSlice";
import CommonSearch from "../../components/ui/CommonSearch/CommonSearch.jsx";
import WalletTable from "./WalletTable/WalletTable.jsx";
import WalletTransactions from "./WalletTransactions/WalletTransactions.jsx";
import PortfolioAssetAllocationChart from "../../components/charts/PortfolioAssetAllocationChart.jsx";
import PortfolioBalanceLineChart from "../../components/charts/PortfolioBalanceLineChart.jsx";
import { parseJSON } from "../../helpers/commonHelper";
import { normalizePortfolioGraph, normalizeAssetAllocation, applyPortfolioChartRange, PORTFOLIO_GRAPH_API_RANGE, formatPortfolioUsdAmount, normalizeWalletListResponse, WALLET_LIST_PAGE_SIZE } from "./chartDataUtils";
const WALLET_ICONS = ["assets/matic.png", "assets/eth.png", "assets/apt.png"];
const Portfolio = () => {
  const dispatch = useDispatch();
  const [balanceChartRange, setBalanceChartRange] = useState("6m");
  const [walletRows, setWalletRows] = useState([]);
  const [walletTotalCount, setWalletTotalCount] = useState(0);
  const [walletPage, setWalletPage] = useState(1);
  const [assetWalletData, setAssetWalletData] = useState([]);
  const [assetTotalUsdValue, setassetTotalUsdValue] = useState(0);
  const [immediateWalletSearch, setImmediateWalletSearch] = useState("");
  const [debouncedWalletSearch, setDebouncedWalletSearch] = useState("");
  const [graphRawLabels, setGraphRawLabels] = useState([]);
  const [graphRawValues, setGraphRawValues] = useState([]);
  const [graphLoading, setGraphLoading] = useState(false);
  const [allocationItems, setAllocationItems] = useState(null);
  const [allocationTotalUsd, setAllocationTotalUsd] = useState(null);
  const [portfolioBalance, setPortfolioBalance] = useState(0);
  const [walletSearchLoading, setWalletSearchLoading] = useState(false);
  const [walletSearchClearSpin, setWalletSearchClearSpin] = useState(false);
  const [walletTableReady, setWalletTableReady] = useState(false);
  const loadPortfolioGraph = useCallback(async () => {
    setGraphLoading(true);
    try {
      const graphRes = await dispatch(ATS.fetchPortfolioBalanceGraph({
        range: PORTFOLIO_GRAPH_API_RANGE
      }));
      const g = normalizePortfolioGraph(graphRes);
      setPortfolioBalance(g.currentBalanceUsd ?? 0);
      setGraphRawLabels(g.labels);
      setGraphRawValues(g.values);
    } finally {
      setGraphLoading(false);
    }
  }, [dispatch]);
  const {
    labels: graphLabels,
    values: graphValues
  } = useMemo(() => applyPortfolioChartRange(graphRawLabels, graphRawValues, balanceChartRange), [graphRawLabels, graphRawValues, balanceChartRange]);
  const loadAssetAllocation = useCallback(async () => {
    const res = await dispatch(ATS.fetchAssetAllocation());
    const n = normalizeAssetAllocation(res);
    setAllocationItems(n.items);
    setAllocationTotalUsd(n.totalUsd);
  }, [dispatch]);
  const pieDataSource = useMemo(() => {
    if (allocationItems === null) {
      return assetWalletData;
    }
    const apiHasPositiveSlice = allocationItems.some(x => Number(x.usd_value) > 0);
    const apiTotalPositive = allocationTotalUsd != null && Number(allocationTotalUsd) > 0;
    const apiHasTokenQty = allocationItems.some(x => Number(x.tokenBalance) > 0);
    if (apiHasPositiveSlice || apiTotalPositive) {
      return allocationItems;
    }
    if (apiHasTokenQty) {
      return allocationItems;
    }
    const walletHasPositive = assetWalletData.some(x => Number(x.usd_value) > 0);
    if (walletHasPositive) {
      return assetWalletData;
    }
    return allocationItems;
  }, [allocationItems, allocationTotalUsd, assetWalletData]);
  const displayTotalUsd = useMemo(() => {
    if (allocationItems === null) {
      return assetTotalUsdValue;
    }
    const apiHasPositiveSlice = allocationItems.some(x => Number(x.usd_value) > 0);
    const apiTotalPositive = allocationTotalUsd != null && Number(allocationTotalUsd) > 0;
    const apiHasTokenQty = allocationItems.some(x => Number(x.tokenBalance) > 0);
    if (apiHasPositiveSlice || apiTotalPositive) {
      if (allocationTotalUsd != null && Number.isFinite(Number(allocationTotalUsd))) {
        return Number(allocationTotalUsd);
      }
      return allocationItems.reduce((s, x) => s + Number(x.usd_value || 0), 0);
    }
    if (apiHasTokenQty) {
      return assetTotalUsdValue;
    }
    const walletHasPositive = assetWalletData.some(x => Number(x.usd_value) > 0);
    if (walletHasPositive) {
      return assetTotalUsdValue;
    }
    const t = Number(allocationTotalUsd);
    return Number.isFinite(t) ? t : assetTotalUsdValue;
  }, [allocationItems, allocationTotalUsd, assetWalletData, assetTotalUsdValue]);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedWalletSearch(immediateWalletSearch), 400);
    return () => clearTimeout(id);
  }, [immediateWalletSearch]);
  useLayoutEffect(() => {
    setWalletPage(1);
  }, [debouncedWalletSearch]);
  const getWalletData = useCallback(async () => {
    setWalletSearchLoading(true);
    let assetData = [];
    let totalUsdValue = 0;
    const params = {
      limit: WALLET_LIST_PAGE_SIZE,
      page: walletPage
    };
    if (debouncedWalletSearch !== "") {
      params.searchText = debouncedWalletSearch;
    }
    try {
      let result = await dispatch(ATS.fetchWalletList(params, {
        skipGlobalLoader: true
      }));
      if (result.status === 200) {
        const {
          rows: walletList,
          totalCount
        } = normalizeWalletListResponse(result);
        walletList.forEach((ele, index) => {
          const globalIndex = (walletPage - 1) * WALLET_LIST_PAGE_SIZE + index;
          ele.tokenImg = WALLET_ICONS[globalIndex % WALLET_ICONS.length];
        });
        setWalletRows(parseJSON(walletList));
        setWalletTotalCount(totalCount);
        walletList.forEach(element => {
          if (Array.isArray(element.assetList) && element.assetList.length > 0) {
            element.assetList.forEach(innerElement => {
              totalUsdValue = Number(totalUsdValue) + Number(innerElement.available_usd_value || 0);
              assetData.push({
                asset_name: innerElement.asset_name,
                usd_value: innerElement.available_usd_value
              });
            });
          }
        });
        totalUsdValue = Number(totalUsdValue.toFixed(4));
        setassetTotalUsdValue(totalUsdValue);
        setAssetWalletData(assetData);
        setWalletTableReady(true);
      } else {
        setWalletRows([]);
        setWalletTotalCount(0);
      }
    } finally {
      setWalletSearchLoading(false);
      setWalletSearchClearSpin(false);
    }
  }, [dispatch, debouncedWalletSearch, walletPage]);
  useEffect(() => {
    getWalletData();
  }, [getWalletData]);
  useEffect(() => {
    loadAssetAllocation();
  }, [loadAssetAllocation]);
  useEffect(() => {
    loadPortfolioGraph();
  }, [loadPortfolioGraph]);
  const handleWalletSearchClear = useCallback(() => {
    setWalletSearchClearSpin(true);
  }, []);
  const walletSearchShowSpinner = walletTableReady && (walletSearchClearSpin || walletSearchLoading && (immediateWalletSearch !== "" || debouncedWalletSearch !== ""));
  return <>
      <div className="userPortfolio">
        <div className="userPortfolio__page">
          <Row className="userPortfolio__top g-2">
            <Col xs={12} xl={7} className="d-flex">
              <div className="portfolioBalance commonCardbox w-100">
                <div className="portfolioBalance__head d-flex align-items-start justify-content-between">
                  <div className="portfolioBalance__title">
                    <div className="dashHeading">
                      <h3>Portfolio Balance</h3>
                    </div>
                    <p className="portfolioBalance__value">
                      {formatPortfolioUsdAmount(portfolioBalance || 0)}
                    </p>
                  </div>
                  <ul className="d-flex portfolioBalance__ranges">
                    <li>
                      <button type="button" className={balanceChartRange === "1m" ? "active" : ""} onClick={() => setBalanceChartRange("1m")}>
                        1m
                      </button>
                    </li>
                    <li>
                      <button type="button" className={balanceChartRange === "3m" ? "active" : ""} onClick={() => setBalanceChartRange("3m")}>
                        3m
                      </button>
                    </li>
                    <li>
                      <button type="button" className={balanceChartRange === "6m" ? "active" : ""} onClick={() => setBalanceChartRange("6m")}>
                        6m
                      </button>
                    </li>
                    <li>
                      <button type="button" className={balanceChartRange === "1y" ? "active" : ""} onClick={() => setBalanceChartRange("1y")}>
                        1y
                      </button>
                    </li>
                    <li>
                      <button type="button" className={balanceChartRange === "All" ? "active" : ""} onClick={() => setBalanceChartRange("All")}>
                        All
                      </button>
                    </li>
                  </ul>
                </div>
                <PortfolioBalanceLineChart labels={graphLabels} values={graphValues} loading={graphLoading} range={balanceChartRange} />
              </div>
            </Col>
            <Col xs={12} xl={5} className="d-flex">
              <div className="assetAllocation commonCardbox w-100">
                <div className="assetAllocation__head">
                  <div className="dashHeading">
                    <h3>Asset Allocation</h3>
                  </div>
                </div>
                <div className="pieChart">
                  <PortfolioAssetAllocationChart assetWalletData={pieDataSource} assetTotalUsdValue={displayTotalUsd} />
                </div>
              </div>
            </Col>
          </Row>
          <div className="totalWallet commonCardbox">
            <div className="totalWallet__head d-sm-flex align-items-center justify-content-between">
              <div className="dashHeading">
                <h3>Total Wallets</h3>
              </div>
              <CommonSearch className="walletSearch mt-sm-0" placeholder="Search" value={immediateWalletSearch} loading={walletSearchShowSpinner} onClear={handleWalletSearchClear} onChange={e => setImmediateWalletSearch(e.target.value)} />
            </div>
            <WalletTable rows={walletRows} totalCount={walletTotalCount} page={walletPage} limit={WALLET_LIST_PAGE_SIZE} onPageChange={setWalletPage} onWithdrawSuccess={getWalletData} />
          </div>
          <WalletTransactions />
        </div>
      </div>
    </>;
};
export default Portfolio;